import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Badge } from "react-bootstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import { Breadcrumbs } from "@mui/material";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import authAxios from "../Auth/useAxios";
import { ADMIN_API_BASE_URL } from "../Auth/Api";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Base from "../admin_components/Admin_Base";
import {
  FaEye,
  FaEdit,
  FaDownload,
  FaSearch,
  FaFilter,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt
} from "react-icons/fa";

const CustomerList = () => {
  const navigate = useNavigate();
  const [customersList, setCustomersList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCustomerList();
  }, []);

  const fetchCustomerList = () => {
    authAxios
      .get(`${ADMIN_API_BASE_URL}/main/admin/list-customers`)
      .then((response) => {
        if (Array.isArray(response.data)) {
          setCustomersList(response.data);
        } else {
          setCustomersList([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching customers:", error);
        setCustomersList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleStatusChange = async (id, newStatus, customerName) => {
    const result = await Swal.fire({
      title: "Change Status?",
      text: `Do you want to change status for ${customerName} to ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const statusBoolean = newStatus === "Active";
        await authAxios.patch(
          `${ADMIN_API_BASE_URL}/main/admin/${id}/status`,
          { status: statusBoolean }
        );
        
        // Update local state
        setCustomersList(prevList =>
          prevList.map(customer =>
            customer.customerId === id 
              ? { ...customer, status: statusBoolean }
              : customer
          )
        );
        
        Swal.fire("Success!", `Status updated to ${newStatus}.`, "success");
      } catch (error) {
        console.error(error.response?.data || error.message);
        Swal.fire("Error!", "Failed to update status.", "error");
      }
    }
  };

  const handleView = (customerId) => {
    navigate(`/admin/customer-view/${customerId}`);
  };

  const handleEdit = (customerId) => {
    navigate(`/admin/customer-edit/${customerId}`);
  };

  const handleDownloadExcel = () => {
    const dataToExport = customersList.map(customer => ({
      "Customer ID": customer.customerId,
      "Name": customer.customerName,
      "Mobile": customer.mobileNumber,
      "Email": customer.email,
      "City": customer.city || "N/A",
      "Status": customer.status ? "Active" : "Inactive",
      "Created Date": customer.createdDate ? new Date(customer.createdDate).toLocaleDateString() : "N/A"
    }));

    if (dataToExport.length === 0) {
      Swal.fire("No Data!", "There are no customers to export.", "info");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");
    XLSX.writeFile(wb, `Customers_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Filter customers based on search and status
  const filteredCustomers = customersList.filter((customer) => {
    const matchesSearch =
      customer.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.mobileNumber?.includes(searchQuery) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && customer.status) ||
      (statusFilter === "inactive" && !customer.status);

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats calculation
  const activeCustomers = customersList.filter(cust => cust.status).length;
  const totalCustomers = customersList.length;

  return (
    <Base>
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
          <ClipLoader color="#2E8B57" size={50} />
        </div>
      ) : (
        <>
          {/* Header Section */}
          <div className="container-fluid pt-3 mt-5">
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/admin/dashboard" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
                Dashboard
              </Link>
              <Link underline="none" color="inherit" sx={{ color: "darkslategrey", fontWeight: "bold" }}>
                Customer Management
              </Link>
            </Breadcrumbs>
          </div>

          <div className="container-fluid mt-3">
            {/* Page Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="fw-bold text-dark mb-1">Customer Management</h2>
                <p className="text-muted mb-0">Manage all customer accounts and information</p>
              </div>
              <Button 
                variant="success" 
                onClick={handleDownloadExcel}
                className="d-flex align-items-center"
              >
                <FaDownload className="me-2" />
                Export Excel
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4">
              <div className="col-md-3">
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="card-title text-muted mb-2">Total Customers</h6>
                        <h3 className="fw-bold text-primary">{totalCustomers}</h3>
                      </div>
                      <div className="bg-primary bg-opacity-10 p-3 rounded">
                        <FaUser className="text-primary fs-4" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-3">
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="card-title text-muted mb-2">Active Customers</h6>
                        <h3 className="fw-bold text-success">{activeCustomers}</h3>
                      </div>
                      <div className="bg-success bg-opacity-10 p-3 rounded">
                        <FaUser className="text-success fs-4" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-3">
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="card-title text-muted mb-2">Inactive Customers</h6>
                        <h3 className="fw-bold text-secondary">{totalCustomers - activeCustomers}</h3>
                      </div>
                      <div className="bg-secondary bg-opacity-10 p-3 rounded">
                        <FaUser className="text-secondary fs-4" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div className="col-md-3">
                <Card className="border-0 shadow-sm h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="card-title text-muted mb-2">This Page</h6>
                        <h3 className="fw-bold text-info">{paginatedCustomers.length}</h3>
                      </div>
                      <div className="bg-info bg-opacity-10 p-3 rounded">
                        <FaUser className="text-info fs-4" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>

            {/* Filters Card */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="row g-3 align-items-center">
                  <div className="col-md-5">
                    <div className="position-relative">
                      <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                      <input
                        type="text"
                        className="form-control ps-5 py-2"
                        placeholder="Search customers by name, phone, or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="position-relative">
                      <FaFilter className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                      <select
                        className="form-select ps-5 py-2"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active Only</option>
                        <option value="inactive">Inactive Only</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="text-muted small">
                      Showing {filteredCustomers.length} of {totalCustomers} customers
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Customers Table */}
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table className="table table-hover mb-0">
                    <Thead className="bg-light">
                      <Tr>
                        <Th className="border-0 ps-4">CUSTOMER</Th>
                        <Th className="border-0">CONTACT</Th>
                        <Th className="border-0">LOCATION</Th>
                        <Th className="border-0">STATUS</Th>
                        <Th className="border-0 text-end pe-4">ACTIONS</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedCustomers.length > 0 ? (
                        paginatedCustomers.map((customer, index) => (
                          <Tr key={customer.customerId} className="align-middle">
                            <Td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                  <FaUser className="text-primary fs-6" />
                                </div>
                                <div>
                                  <div className="fw-semibold text-dark">{customer.customerName}</div>
                                  <small className="text-muted">ID: {customer.customerId}</small>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div>
                                <div className="d-flex align-items-center mb-1">
                                  <FaPhone className="text-muted me-2 fs-6" />
                                  <span className="text-dark">{customer.mobileNumber}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                  <FaEnvelope className="text-muted me-2 fs-6" />
                                  <small className="text-muted">{customer.email || "N/A"}</small>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="d-flex align-items-center">
                                <FaMapMarkerAlt className="text-muted me-2 fs-6" />
                                <span>{customer.city || "N/A"}</span>
                              </div>
                            </Td>
                            <Td>
                              <div className="d-flex align-items-center gap-2">
                                <Badge bg={customer.status ? "success" : "secondary"}>
                                  {customer.status ? "Active" : "Inactive"}
                                </Badge>
                                <select
                                  className="form-select form-select-sm"
                                  value={customer.status ? "Active" : "Inactive"}
                                  onChange={(e) => handleStatusChange(customer.customerId, e.target.value, customer.customerName)}
                                  style={{ width: "120px" }}
                                >
                                  <option value="Active">Active</option>
                                  <option value="Inactive">Inactive</option>
                                </select>
                              </div>
                            </Td>
                            <Td className="text-end pe-4">
                              <div className="d-flex justify-content-end gap-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleView(customer.customerId)}
                                  className="d-flex align-items-center"
                                >
                                  <FaEye className="me-1" /> View
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleEdit(customer.customerId)}
                                  className="d-flex align-items-center"
                                >
                                  <FaEdit className="me-1" /> Edit
                                </Button>
                              </div>
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan="5" className="text-center py-5">
                            <div className="text-muted">
                              <FaUser className="fs-1 mb-3 opacity-50" />
                              <h5>No customers found</h5>
                              <p>Try adjusting your search or filter criteria</p>
                            </div>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="text-muted">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} of {filteredCustomers.length} entries
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => (
                      <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </>
      )}
    </Base>
  );
};

export default CustomerList;